import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const METRIC_COLORS = {
  cpu: '#60A5FA',
  memory: '#34D399',
  disk: '#F59E0B',
};

function StatCard({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
      <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-3xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function GaugeBar({ pct, color }) {
  const p = Math.min(100, pct || 0);
  const c = p > 85 ? '#EF4444' : p > 65 ? '#F59E0B' : color;
  return (
    <div className="w-full bg-gray-700 rounded-full h-1.5">
      <div className="h-1.5 rounded-full transition-all" style={{ width: `${p}%`, backgroundColor: c }} />
    </div>
  );
}

// ── Constellation server heartbeat view ──────────────────────────────────────
function ConstellationPanel({ timeRange }) {
  const [servers, setServers] = useState([]);
  const token = localStorage.getItem('operator_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/admin/metrics?hours=${timeRange}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const metrics = data.metrics || [];
        const latest = {};
        metrics.forEach((m) => {
          if (!latest[m.constellation_id] ||
            new Date(m.recorded_at) > new Date(latest[m.constellation_id].recorded_at)) {
            latest[m.constellation_id] = m;
          }
        });
        setServers(Object.values(latest));
      } catch (e) { console.error(e); }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [timeRange, token]);

  const totalCPU = servers.reduce((s, m) => s + (m.cpu_used_cores || 0), 0);
  const totalStorage = servers.reduce((s, m) => s + (m.storage_used_gb || 0), 0);
  const totalInstances = servers.reduce((s, m) => s + (m.active_instances || 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Active Servers" value={servers.length} />
        <StatCard label="Running Instances" value={totalInstances} color="text-blue-400" />
        <StatCard label="CPU Cores Used" value={totalCPU.toFixed(1)} color="text-blue-400" />
        <StatCard label="Storage Used" value={`${totalStorage.toFixed(0)} GB`} color="text-green-400" />
      </div>

      {servers.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-8 text-center text-gray-500 border border-gray-700">
          No constellation heartbeats received yet.
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-900/50">
                {['Server', 'CPU', 'Storage', 'Instances', 'Last Seen'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {servers.map((s) => (
                <tr key={s.constellation_id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium text-sm">{s.constellation_name || s.constellation_id?.slice(0, 8)}</div>
                    {s.region && <div className="text-gray-500 text-xs">{s.region}</div>}
                  </td>
                  <td className="px-4 py-3 w-44">
                    <div className="flex items-center gap-2">
                      <div className="flex-1"><GaugeBar pct={(s.cpu_used_cores / 20) * 100} color={METRIC_COLORS.cpu} /></div>
                      <span className="text-white text-xs">{(s.cpu_used_cores || 0).toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 w-44">
                    <div className="flex items-center gap-2">
                      <div className="flex-1"><GaugeBar pct={(s.storage_used_gb / 500) * 100} color={METRIC_COLORS.disk} /></div>
                      <span className="text-white text-xs">{(s.storage_used_gb || 0).toFixed(0)} GB</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white text-sm">{s.active_instances || 0}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{new Date(s.recorded_at).toLocaleTimeString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Single VM metrics card ────────────────────────────────────────────────────
function VMMetricsCard({ instance, hours, token }) {
  const [pts, setPts] = useState([]);
  const [latest, setLatest] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/v1/instances/${instance.id}/metrics?hours=${hours}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setPts((data.points || []).map((p) => ({
        t: new Date(p.collected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cpu: Number((p.cpu_usage || 0).toFixed(1)),
        mem: Number((p.memory_usage || 0).toFixed(1)),
        disk: Number((p.disk_usage || 0).toFixed(1)),
      })));
      setLatest(data.latest);
    } catch (e) { /* silent */ }
  }, [instance.id, hours, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const l = latest || {};
  const stats = [
    { key: 'CPU', val: l.cpu_usage, color: METRIC_COLORS.cpu },
    { key: 'Mem', val: l.memory_usage, color: METRIC_COLORS.memory },
    { key: 'Disk', val: l.disk_usage, color: METRIC_COLORS.disk },
  ];

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-white font-medium text-sm">{instance.name}</div>
          <div className="text-gray-500 text-xs">{instance.ip_address || '—'} · {instance.instance_type}</div>
        </div>
        <div className="flex gap-4">
          {stats.map(({ key, val, color }) => (
            <div key={key} className="text-center">
              <div className="text-gray-400 text-xs">{key}</div>
              <div className="font-bold text-sm" style={{ color }}>
                {val != null ? `${Number(val).toFixed(0)}%` : '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {pts.length > 1 ? (
        <ResponsiveContainer width="100%" height={64}>
          <LineChart data={pts} margin={{ top: 2, right: 4, left: -32, bottom: 0 }}>
            <XAxis dataKey="t" hide />
            <YAxis domain={[0, 100]} hide />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 11, padding: '3px 8px' }}
              labelStyle={{ display: 'none' }}
            />
            <Line type="monotone" dataKey="cpu" stroke={METRIC_COLORS.cpu} strokeWidth={1.5} dot={false} isAnimationActive={false} name="CPU%" />
            <Line type="monotone" dataKey="mem" stroke={METRIC_COLORS.memory} strokeWidth={1.5} dot={false} isAnimationActive={false} name="Mem%" />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-gray-600 text-xs text-center py-3">
          {pts.length === 0 ? 'No samples yet — check back in ~60 s' : 'Collecting…'}
        </div>
      )}

      {/* gauge bars */}
      <div className="space-y-1.5">
        {stats.map(({ key, val, color }) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-gray-500 text-xs w-8">{key}</span>
            <div className="flex-1"><GaugeBar pct={val} color={color} /></div>
            <span className="text-gray-400 text-xs w-8 text-right">
              {val != null ? `${Number(val).toFixed(0)}%` : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VMMetricsTab({ timeRange }) {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('operator_token');

  useEffect(() => {
    const load = async () => {
      try {
        // Try admin endpoint first; fall back to regular instances list
        const res = await fetch(`${API_URL}/v1/operator/instances`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setInstances((data.instances || []).filter((i) => i.status === 'running'));
        }
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
  }, [token]);

  if (loading) return <div className="text-gray-400 text-center py-12">Loading…</div>;
  if (instances.length === 0) {
    return (
      <div className="text-center text-gray-500 py-12 bg-gray-800 rounded-xl border border-gray-700">
        No running instances. Start a VM to see per-VM metrics here.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {instances.map((inst) => (
        <VMMetricsCard key={inst.id} instance={inst} hours={timeRange} token={token} />
      ))}
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────
const MonitoringDashboard = () => {
  const [tab, setTab] = useState('servers');
  const [timeRange, setTimeRange] = useState('1');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-white">Monitoring</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="bg-gray-700 border border-gray-600 text-white text-sm rounded px-3 py-2"
        >
          <option value="1">Last 1 hour</option>
          <option value="6">Last 6 hours</option>
          <option value="24">Last 24 hours</option>
          <option value="72">Last 3 days</option>
        </select>
      </div>

      <div className="flex border-b border-gray-700 gap-1">
        {[
          { id: 'servers', label: '🖥️ Constellation Servers' },
          { id: 'vms', label: '📊 Per-VM Metrics' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              tab === t.id
                ? 'bg-gray-800 text-white border border-b-0 border-gray-700 -mb-px'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'servers' && <ConstellationPanel timeRange={timeRange} />}
      {tab === 'vms' && <VMMetricsTab timeRange={timeRange} />}
    </div>
  );
};

export default MonitoringDashboard;
